/**
 * Orchestration Service API Client
 * Central client for all orchestration service endpoints
 */

import { useQuery } from '@tanstack/react-query';
import { plannerApi } from './planner';
import { coderApi } from './coder';
import { readinessApi } from './readiness';
// Note: evidenceApi is in ./evidence.ts - import from there directly

// API base URL
const API_BASE = '/api/orchestration';

/**
 * Health check response
 */
export interface HealthResponse {
  status: string;
  timestamp: string;
  uptime: number;
  version: string;
  temporal: {
    connected: boolean;
    namespace: string;
  };
}

/** Martha's current Archie environment context */
export interface CurrentEnvironment {
  environment: string;
  label: string;
  shortLabel: string;
  api: string;
  frontend?: string;
  hosting: 'local' | 'render' | 'azure';
  production: boolean;
}

/**
 * Orchestration service API
 */
export const orchestrationApi = {
  /**
   * Check service health
   */
  async health(): Promise<HealthResponse> {
    const response = await fetch(`${API_BASE}/health`);
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Get the Archie environment this Martha instance is configured to target
   */
  async getEnvironment(): Promise<CurrentEnvironment> {
    const response = await fetch(`${API_BASE}/environment`);
    if (!response.ok) throw new Error(`Failed to fetch environment: ${response.statusText}`);
    return response.json();
  },

  // Re-export sub-APIs for convenience
  planner: plannerApi,
  coder: coderApi,
  readiness: readinessApi,
  // Evidence API should be imported directly from ./evidence
};

/** Hook: current Martha Archie environment (cached 5 min, rarely changes) */
export function useCurrentEnvironment() {
  return useQuery({
    queryKey: ['orchestration', 'environment'],
    queryFn: () => orchestrationApi.getEnvironment(),
    staleTime: 5 * 60_000,
  });
}

export interface ArchieEnvironmentStatus {
  id: string; label: string; shortLabel: string; hosting: string;
  production: boolean; status: 'online' | 'offline' | 'unknown';
  skillCount: number; agentCount: number; version?: string; latencyMs?: number;
}

export function useArchieEnvironments() {
  return useQuery({
    queryKey: ['archie-environments'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/archie-environments`);
      if (!res.ok) throw new Error('Failed to fetch environments');
      return res.json() as Promise<{ data: ArchieEnvironmentStatus[] }>;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export default orchestrationApi;
